<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { checkQrCodeEditablePermission } from '@ilteducation/customer-mgmt-permissions';

import SidePanel from '../SidePanel/SidePanel.vue';
import SidePanelSection from '../SidePanel/SidePanelSection.vue';
import EditGroupModal from './EditGroupModal.vue';
import DeleteGroupModal from './DeleteGroupModal.vue';
import GroupLoginModal from './GroupLoginModal.vue';

import { useSidebarGroup } from '../../composables/useSidebarGroup';
import useModal, { modals } from '../../composables/useModal';
import ManageMembers from './ManageMembers/ManageMembers.vue';
import GroupInfo from './GroupInfo.vue';
import SidePanelSectionItem from '../SidePanel/SidePanelSectionItem.vue';
import { useLocale } from '../../composables/useLocale';
import SetGroupPasswordModal from './SetGroupPasswordModal.vue';
import ConfirmDeleteGroupCodeModal from './ConfirmDeleteGroupCodeModal.vue';
import SidePanelSectionButtonItem from '../SidePanel/SidePanelSectionButtonItem.vue';
import CreateMultipleLoginCardsModal from '../LoginCard/CreateMultipleLoginCardsModal.vue';
import { useCurrentOrganization } from '../../composables/useCurrentOrganization';
import { useCreateLoginCards } from '../LoginCard/useCreateLoginCards';
import { usePermissions } from '../../composables/usePermissions';

const { t } = useLocale();
const { organization } = useCurrentOrganization();

type Props = {
  groupUid: string;
  schoolUid: string;
};

const props = defineProps<Props>();
const emit = defineEmits(['close', 'groupDeleted', 'groupUpdated']);

const { group, members, getGroup, isGroupLoading } = useSidebarGroup();
const { open: openEditGroupModal } = useModal(modals.EDIT_GROUP);
const { open: openEditGroupLoginModal } = useModal(modals.EDIT_GROUP_LOGIN);
const { close: closeConfirmDeleteGroupCodeModal } = useModal(modals.CONFIRM_DELETE_GROUP_CODE);
const { close: closeManageUserContentSettings } = useModal(modals.MANAGE_CONTENT_SETTINGS);

const { createLoginCardsForGroupUsers } = useCreateLoginCards();
const { permissionContext, getCallerAssociation, reporterBoolean } = usePermissions();

const sidePanel = ref();
const schoolName = ref('');

const isQrCodeEditable = ref<ReturnType<typeof reporterBoolean>>(false);
watch(
  [props.groupUid, props.schoolUid],
  async () => {
    await getGroup(props.groupUid, props.schoolUid);
    schoolName.value = (group.value as any)?.schoolUnit?.name ?? schoolName.value;

    checkQrCodeEditablePermission(
      permissionContext.value,
      { organization: organization.value, role: 'student' },
      getCallerAssociation,
      reporterBoolean,
    ).then((result) => {
      isQrCodeEditable.value = result;
    });
  },
  { immediate: true },
);

const title = computed(() => group.value?.name ?? '');
const subtitle = computed(() => t('lcp.group_in_school', { school: group.value?.schoolUnit?.name ?? '' }));

const groupUpdated = () => {
  // make typescript happy
  if (!group.value || !group.value.uid) {
    return;
  }

  getGroup(group.value.uid, props.schoolUid, true);
  emit('groupUpdated', group.value?.uid);
};
const groupDeleted = () => {
  emit('groupDeleted');
  emit('close');
};

const manageLoginModal = ref<InstanceType<typeof GroupLoginModal> | null>(null);
const cancelDeleteGroupCodeModal = () => {
  openEditGroupLoginModal();
  closeConfirmDeleteGroupCodeModal();
};

const deleteGroupCode = () => {
  closeConfirmDeleteGroupCodeModal();
  openEditGroupLoginModal();
  setTimeout(() => {
    manageLoginModal.value?.removeGroupcode();
  }, 100);
};
</script>

<template>
  <SidePanel
    ref="sidePanel"
    selected-entity="group"
    :selected-entity-uid="groupUid"
    :school-uid="schoolUid"
    :title="title"
    :subtitle="subtitle"
    icon="mdi-account-multiple"
    :is-loading="isGroupLoading"
    :title-action="openEditGroupModal"
    :on-close="() => emit('close')"
  >
    <SidePanelSection :title="t('lcp.group_login')" :action="openEditGroupLoginModal" :loading="isGroupLoading">
      <SidePanelSectionItem
        :text="group?.code ?? ''"
        icon="mdi-account-multiple"
        :tooltip-text="t('lcp.group_code')"
        :loading="isGroupLoading"
      />
      <SidePanelSectionButtonItem
        v-if="group && isQrCodeEditable"
        :text="t('lcp.generate_login_cards')"
        icon="mdi-qrcode"
        :tooltip-text="t('lcp.create_qr_code_for_users')"
        @click="() => createLoginCardsForGroupUsers(groupUid, { fileName: group?.name })"
      />
    </SidePanelSection>
    <ManageMembers v-if="group" :group="group" :loading="isGroupLoading" />
    <slot name="settings" :group="group" :members="members"></slot>
    <GroupInfo :loading="isGroupLoading" />
  </SidePanel>
  <EditGroupModal v-if="group" :name="modals.EDIT_GROUP" :group="group" @group-updated="groupUpdated" />
  <DeleteGroupModal v-if="group" :name="modals.DELETE_GROUP" :group="group" @group-deleted="groupDeleted" />
  <GroupLoginModal
    v-if="group"
    ref="manageLoginModal"
    :name="modals.EDIT_GROUP_LOGIN"
    :group="group"
    @group-updated="groupUpdated"
  />
  <ConfirmDeleteGroupCodeModal
    v-if="group"
    :name="modals.CONFIRM_DELETE_GROUP_CODE"
    :is-loading="isGroupLoading"
    @delete="deleteGroupCode"
    @close="cancelDeleteGroupCodeModal"
  />
  <SetGroupPasswordModal v-if="group" :name="modals.SET_GROUP_PASSWORD" :group="group" />
  <CreateMultipleLoginCardsModal createQrCodeSelected />
  <slot
    v-if="group"
    name="settings-modal"
    :group="group"
    :members="members"
    :organization-uid="schoolUid"
    :close="closeManageUserContentSettings"
  ></slot>
</template>
<style scoped lang="scss"></style>
